import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { TrendingUp, Bot, Shield, Users, Zap, CheckCircle, Star, Menu, X, DollarSign, Wrench, Camera, FileText, ConciergeBell, PiggyBank, Calendar, Package, TrendingDown, Mail, Send, MapPin, Phone, Instagram, Linkedin, Youtube, Lock, CreditCard, RefreshCcw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { motion } from 'framer-motion';
import LanguageSelector from '../components/LanguageSelector';
import { useLanguage } from '../context/LanguageContext';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import api from '../lib/api';
import AgencyDemoModal from '../components/AgencyDemoModal';
import RequestDemoModal from '../components/RequestDemoModal';
import RoiGuidedDemoModal from '../components/RoiGuidedDemoModal';

const HOME_TITLE = 'VillaPilot AI · Luxury Villa Portfolio Intelligence';
const HOME_DESCRIPTION = 'AI-powered portfolio intelligence for luxury villa owners and managers. Smart Alerts, tax estimation, market scanner, valuation and unified reporting.';
const HOME_CANONICAL = 'https://villapilot.ai/';

const MarketingWebsite = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [demoControlTowerOpen, setDemoControlTowerOpen] = useState(false);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [agencyDemoOpen, setAgencyDemoOpen] = useState(false);
  const [roiTourOpen, setRoiTourOpen] = useState(false);
  const [requestDemoOpen, setRequestDemoOpen] = useState(false);
  const [requestDemoSource, setRequestDemoSource] = useState('hero');
  const openRequestDemo = (source) => {
    setRequestDemoSource(source || 'hero');
    setRequestDemoOpen(true);
  };
  const [pricingAudience, setPricingAudience] = useState('owners'); // 'owners' | 'managers'
  const [pricingAnnual, setPricingAnnual] = useState(false); // for Agency annual discount
  const { t, language, setLanguage } = useLanguage();
  const { toast } = useToast();
  
  // Contact form state
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [sendingContact, setSendingContact] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);
  const [contactError, setContactError] = useState('');

  const validateContactForm = () => {
    if (contactForm.name.trim().length < 2) {
      return language === 'es' ? 'El nombre debe tener al menos 2 caracteres.' : 'Name must be at least 2 characters.';
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactForm.email)) {
      return language === 'es' ? 'Ingresa un email válido.' : 'Enter a valid email.';
    }
    if (contactForm.subject.trim().length < 3) {
      return language === 'es' ? 'El asunto debe tener al menos 3 caracteres.' : 'Subject must be at least 3 characters.';
    }
    if (contactForm.message.trim().length < 10) {
      return language === 'es' ? 'El mensaje debe tener al menos 10 caracteres.' : 'Message must be at least 10 characters.';
    }
    return null;
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactError('');
    setContactSuccess(false);

    const validationError = validateContactForm();
    if (validationError) {
      setContactError(validationError);
      return;
    }

    setSendingContact(true);
    
    try {
      const response = await api.post('/contact', {
        name: contactForm.name.trim(),
        email: contactForm.email.trim(),
        subject: contactForm.subject.trim(),
        message: contactForm.message.trim()
      });
      
      if (response.status >= 200 && response.status < 300) {
        setContactSuccess(true);
        setContactForm({ name: '', email: '', subject: '', message: '' });
        toast({
          title: language === 'es' ? '¡Mensaje Enviado!' : 'Message Sent!',
          description: language === 'es' 
            ? 'Te responderemos pronto. Revisa tu carpeta de spam/junk si no ves nuestra respuesta.' 
            : 'We will respond soon. Please check your spam/junk folder if you don\'t see our reply.'
        });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      const detail = error?.response?.data?.detail;
      let errorMsg;
      if (Array.isArray(detail)) {
        errorMsg = detail.map(d => d.msg).join('. ');
      } else if (typeof detail === 'string') {
        errorMsg = detail;
      } else {
        errorMsg = language === 'es' ? 'Error al enviar el mensaje. Intenta de nuevo.' : 'Failed to send message. Please try again.';
      }
      setContactError(errorMsg);
      toast({
        title: 'Error',
        description: errorMsg,
        variant: 'destructive'
      });
    } finally {
      setSendingContact(false);
    }
  };

  const features = [
    {
      icon: TrendingUp,
      titleKey: "featureFinancialTitle",
      descriptionKey: "featureFinancialDesc"
    },
    {
      icon: Bot,
      titleKey: "featureAITitle",
      descriptionKey: "featureAIDesc"
    },
    {
      icon: Users,
      titleKey: "featureMultiRoleTitle",
      descriptionKey: "featureMultiRoleDesc"
    },
    {
      icon: Shield,
      titleKey: "featureSecurityTitle",
      descriptionKey: "featureSecurityDesc"
    },
    {
      icon: Zap,
      titleKey: "featureRealtimeTitle",
      descriptionKey: "featureRealtimeDesc"
    },
    {
      icon: CheckCircle,
      titleKey: "featureOperationsTitle",
      descriptionKey: "featureOperationsDesc"
    }
  ];

  // Dynamic pricing plans — split into Owners vs Managers groups (see toggle in Pricing section)
  const getPricingPlans = () => ({
    owners: [
      {
        name: "Starter",
        price: "$49",
        period: language === 'es' ? "/mes" : "/month",
        icon: "building",
        features: [
          language === 'es' ? "1 Propiedad" : "1 Property",
          language === 'es' ? "1 Manager" : "1 Manager",
          language === 'es' ? "Dashboard Financiero" : "Financial Dashboard",
          language === 'es' ? "Seguimiento de Gastos" : "Expense Tracking",
          language === 'es' ? "Reportes Básicos" : "Basic Reports",
          language === 'es' ? "Soporte por Email" : "Email Support"
        ]
      },
      {
        name: "Pro",
        price: "$99",
        period: language === 'es' ? "/mes" : "/month",
        popular: true,
        icon: "zap",
        features: [
          language === 'es' ? "Hasta 3 Propiedades" : "Up to 3 Properties",
          language === 'es' ? "AI CFO Completo (ROI, Cash Flow)" : "Complete AI CFO (ROI, Cash Flow)",
          language === 'es' ? "Reportes + Mantenimiento" : "Reports + Maintenance",
          language === 'es' ? "Managers Ilimitados" : "Unlimited Managers",
          language === 'es' ? "Inventario Visual con AI" : "Visual Inventory with AI",
          language === 'es' ? "Soporte Prioritario" : "Priority Support"
        ]
      },
      {
        name: "Premium",
        price: "$199",
        period: language === 'es' ? "/mes" : "/month",
        icon: "crown",
        features: [
          language === 'es' ? "Hasta 10 Propiedades" : "Up to 10 Properties",
          language === 'es' ? "AI Completo + Insights" : "Full AI + Insights",
          language === 'es' ? "Reportes Avanzados" : "Advanced Reports",
          language === 'es' ? "Vendor Network" : "Vendor Network",
          language === 'es' ? "Soporte Prioritario 24/7" : "24/7 Priority Support",
          language === 'es' ? "Acceso API" : "API Access"
        ]
      }
    ],
    managers: [
      {
        name: "Manager Pro",
        price: "$29",
        period: language === 'es' ? "/propiedad/mes" : "/property/month",
        icon: "users",
        isManagerPlan: true,
        features: [
          language === 'es' ? "Gestión de Múltiples Propiedades" : "Manage Multiple Properties",
          language === 'es' ? "Subir Gastos y Facturas" : "Upload Expenses & Invoices",
          language === 'es' ? "Mantenimiento" : "Maintenance",
          language === 'es' ? "Inventario" : "Inventory",
          language === 'es' ? "Invitar Owners" : "Invite Owners",
          language === 'es' ? "Sin Finanzas Completas" : "No Full Financials"
        ]
      },
      {
        name: "Agency",
        // price is dynamic based on annual toggle — handled in render
        subtitleEs: "Diseñado para portafolios, no villas individuales",
        subtitleEn: "Built for portfolios, not single villas",
        icon: "users",
        isAgencyPlan: true,
        priceMonthly: 180,
        priceAnnualPerVilla: 153, // 15% off
        minProperties: 10,
        features: [
          language === 'es' ? "Todo lo de Premium, para cada villa de tu portafolio" : "Everything in Premium, for every villa in your portfolio",
          language === 'es' ? "Dashboard unificado multi-propiedad" : "Unified multi-property dashboard",
          language === 'es' ? "Precio por volumen — a mayor portafolio, menor costo por villa" : "Volume-based pricing — the bigger your portfolio, the lower your per-villa cost",
          language === 'es' ? "Onboarding dedicado + gerente de cuenta asignado" : "Dedicated onboarding + account manager",
          language === 'es' ? "Reportes consolidados para dueños e inversionistas" : "Consolidated reports for owners & investors",
          language === 'es' ? "Contrato anual: 15% de descuento por prepago" : "Annual contract: 15% prepay discount"
        ]
      }
    ]
  });

  const pricingPlans = getPricingPlans();

  // Dynamic testimonials with translations
  const getTestimonials = () => [
    {
      name: "Carlos Mendez",
      role: language === 'es' ? "Propietario de Villa, Costa Rica" : "Villa Owner, Costa Rica",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop",
      text: language === 'es' 
        ? "VillaPilot AI transformó la forma en que administro mis 3 villas de lujo. Solo el asistente IA me ahorra 10 horas a la semana."
        : "VillaPilot AI transformed how I manage my 3 luxury villas. The AI assistant alone saves me 10 hours a week."
    },
    {
      name: "Sofia Martinez",
      role: language === 'es' ? "Administradora de Propiedades, México" : "Property Manager, Mexico",
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop",
      text: language === 'es'
        ? "Administrar 12 propiedades nunca ha sido tan fácil. La app móvil me permite aprobar gastos y crear tickets de mantenimiento sobre la marcha."
        : "Managing 12 properties has never been easier. The mobile app lets me approve expenses and create maintenance tickets on the go."
    },
    {
      name: "James Wilson",
      role: language === 'es' ? "Inversionista Inmobiliario, USA" : "Real Estate Investor, USA",
      image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop",
      text: language === 'es'
        ? "La transparencia financiera y los reportes en tiempo real me dan total confianza en mis inversiones inmobiliarias."
        : "The financial transparency and real-time reporting gives me complete confidence in my property investments."
    }
  ];

  const testimonials = getTestimonials();

  return (
    <div className="min-h-screen bg-sand-warm">
      <Helmet>
        <title>{HOME_TITLE}</title>
        <meta name="description" content={HOME_DESCRIPTION} />
        <link rel="canonical" href={HOME_CANONICAL} />
        <meta property="og:url" content={HOME_CANONICAL} />
        <meta property="og:title" content={HOME_TITLE} />
        <meta property="og:description" content={HOME_DESCRIPTION} />
      </Helmet>
      {/* Demo Modal */}
      {showDemoModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto">
          <div className="relative bg-white rounded-sm shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto my-8">
            {/* Close Button */}
            <button 
              onClick={() => {
                setShowDemoModal(false);
                setDemoControlTowerOpen(false);
              }}
              className="absolute top-4 right-4 z-10 p-2 bg-white/90 hover:bg-white rounded-full shadow-lg"
            >
              <X className="w-6 h-6" />
            </button>
            
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-navy-deep to-navy-deep/90 text-white p-6">
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-gold-text mb-2">{t('liveDemo')}</p>
              <h2 className="text-2xl md:text-3xl font-playfair font-medium">
                {t('seeVillaPilotInAction')}
              </h2>
              <p className="text-white/70 mt-2">{t('exploreRealDemoProperty')}</p>
            </div>
            
            {/* Property Card */}
            <div className="flex flex-col md:flex-row border-b">
              <div className="md:w-64 h-48 md:h-auto relative overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=800&q=80"
                  alt="Villa Claude Demo"
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-3 left-3 bg-gold-metallic text-navy-deep text-xs font-bold px-3 py-1 rounded">
                  DEMO
                </div>
              </div>
              <div className="flex-1 p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-xl font-playfair font-medium text-foreground mb-1">
                      Villa Claude (Demo)
                    </h3>
                    <p className="text-sm text-muted-foreground mb-3">Playa Hermosa, Guanacaste, Costa Rica</p>
                    <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                      <span>🛏️ 5 {t('beds')}</span>
                      <span>🚿 5 {t('baths')}</span>
                      <span>📐 450m²</span>
                    </div>
                  </div>
                  
                  {/* Control Tower Button */}
                  <button 
                    onClick={() => setDemoControlTowerOpen(!demoControlTowerOpen)}
                    className={`p-2 rounded-sm transition-colors ${demoControlTowerOpen ? 'bg-navy-deep text-white' : 'hover:bg-muted border border-border'}`}
                    title={t('openControlTower')}
                  >
                    {demoControlTowerOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            </div>
            
            {/* Control Tower Panel */}
            {demoControlTowerOpen && (
              <div className="border-b bg-navy-deep/5 p-5">
                <h4 className="text-base font-playfair font-medium mb-3 flex items-center gap-2 text-navy-deep">
                  <Menu className="w-4 h-4" />
                  {t('controlTowerManageProperty')}
                </h4>
                
                <div className="bg-gradient-to-r from-gold-metallic/10 to-gold-metallic/5 border border-gold-metallic/30 rounded-sm p-3 mb-4">
                  <p className="text-xs text-gold-metallic font-medium">
                    📋 {t('allToolsNeeded')}
                  </p>
                </div>
                
                {/* Quick Actions */}
                <div className="grid grid-cols-4 gap-2 mb-4">
                  <div className="flex flex-col items-center gap-1 p-3 bg-white rounded-sm border text-center">
                    <DollarSign className="w-5 h-5 text-emerald-600" />
                    <span className="text-[10px] font-medium">{t('income')}</span>
                  </div>
                  <div className="flex flex-col items-center gap-1 p-3 bg-white rounded-sm border text-center">
                    <TrendingDown className="w-5 h-5 text-red-500" />
                    <span className="text-[10px] font-medium">{t('expensesLabel')}</span>
                  </div>
                  <div className="flex flex-col items-center gap-1 p-3 bg-white rounded-sm border text-center">
                    <Calendar className="w-5 h-5 text-blue-500" />
                    <span className="text-[10px] font-medium">{t('booking')}</span>
                  </div>
                  <div className="flex flex-col items-center gap-1 p-3 bg-white rounded-sm border text-center">
                    <Package className="w-5 h-5 text-purple-500" />
                    <span className="text-[10px] font-medium">{t('inventory')}</span>
                  </div>
                </div>
                
                {/* Management Sections */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <div className="bg-white p-3 rounded-sm border">
                    <h5 className="text-xs font-medium mb-2 flex items-center gap-1">
                      <PiggyBank className="w-3 h-3 text-gold-metallic" /> AI CFO
                    </h5>
                    <ul className="text-[10px] text-muted-foreground space-y-0.5">
                      <li>• {t('financialDashboard')}</li>
                      <li>• {t('projections')}</li>
                      <li>• {t('smartAlerts')}</li>
                    </ul>
                  </div>
                  <div className="bg-white p-3 rounded-sm border">
                    <h5 className="text-xs font-medium mb-2 flex items-center gap-1">
                      <DollarSign className="w-3 h-3 text-emerald-600" /> {t('expenses')}
                    </h5>
                    <ul className="text-[10px] text-muted-foreground space-y-0.5">
                      <li>• {t('trackIncome')}</li>
                      <li>• {t('categorize')}</li>
                      <li>• {t('setBudgets')}</li>
                    </ul>
                  </div>
                  <div className="bg-white p-3 rounded-sm border">
                    <h5 className="text-xs font-medium mb-2 flex items-center gap-1">
                      <Wrench className="w-3 h-3 text-orange-500" /> {t('maintenanceCat')}
                    </h5>
                    <ul className="text-[10px] text-muted-foreground space-y-0.5">
                      <li>• {t('scheduleTasks')}</li>
                      <li>• {t('trackRepairs')}</li>
                      <li>• {t('history')}</li>
                    </ul>
                  </div>
                  <div className="bg-white p-3 rounded-sm border">
                    <h5 className="text-xs font-medium mb-2 flex items-center gap-1">
                      <Camera className="w-3 h-3 text-blue-500" /> {t('inventory')}
                    </h5>
                    <ul className="text-[10px] text-muted-foreground space-y-0.5">
                      <li>• {t('photoScanning')}</li>
                      <li>• {t('aiRecognition')}</li>
                      <li>• {t('damageReports')}</li>
                    </ul>
                  </div>
                  <div className="bg-white p-3 rounded-sm border">
                    <h5 className="text-xs font-medium mb-2 flex items-center gap-1">
                      <FileText className="w-3 h-3 text-navy-deep" /> {t('reports')}
                    </h5>
                    <ul className="text-[10px] text-muted-foreground space-y-0.5">
                      <li>• {t('financial')}</li>
                      <li>• {t('insurance')}</li>
                      <li>• {t('resale')}</li>
                    </ul>
                  </div>
                  <div className="bg-white p-3 rounded-sm border">
                    <h5 className="text-xs font-medium mb-2 flex items-center gap-1">
                      <ConciergeBell className="w-3 h-3 text-gold-metallic" /> {t('concierge')}
                    </h5>
                    <ul className="text-[10px] text-muted-foreground space-y-0.5">
                      <li>• {t('vendorDirectory')}</li>
                      <li>• {t('requestQuotes')}</li>
                      <li>• {t('contracts')}</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
            
            {/* Demo KPIs */}
            <div className="p-5 bg-gradient-to-b from-muted/10 to-white">
              <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
                {t('monthPerformance12')}
              </h4>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                <div className="bg-white p-3 rounded-sm border text-center">
                  <p className="text-[9px] text-muted-foreground uppercase">{t('balance')}</p>
                  <p className="text-lg font-mono font-semibold text-emerald-600">$12.5k</p>
                </div>
                <div className="bg-white p-3 rounded-sm border text-center">
                  <p className="text-[9px] text-muted-foreground uppercase">{t('income')}</p>
                  <p className="text-lg font-mono font-semibold text-emerald-600">$152k</p>
                </div>
                <div className="bg-white p-3 rounded-sm border text-center">
                  <p className="text-[9px] text-muted-foreground uppercase">{t('expensesLabel')}</p>
                  <p className="text-lg font-mono font-semibold text-red-500">$62k</p>
                </div>
                <div className="bg-white p-3 rounded-sm border text-center">
                  <p className="text-[9px] text-muted-foreground uppercase">{t('occupancy')}</p>
                  <p className="text-lg font-mono font-semibold text-navy-deep">64%</p>
                </div>
                <div className="bg-white p-3 rounded-sm border text-center">
                  <p className="text-[9px] text-muted-foreground uppercase">ROI</p>
                  <p className="text-lg font-mono font-semibold text-gold-metallic">9.2%</p>
                </div>
                <div className="bg-white p-3 rounded-sm border text-center">
                  <p className="text-[9px] text-muted-foreground uppercase">{t('health')}</p>
                  <p className="text-lg font-mono font-semibold text-gold-metallic">92</p>
                </div>
              </div>

              {/* Investment Summary */}
              <div className="bg-navy-deep text-white p-4 rounded-sm mt-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <p className="text-[10px] text-white/60 uppercase">{t('value')}</p>
                    <p className="text-base font-mono font-semibold">$1.25M</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-white/60 uppercase">{t('purchase')}</p>
                    <p className="text-base font-mono font-semibold">$980k</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-white/60 uppercase">{t('appreciation')}</p>
                    <p className="text-base font-mono font-semibold text-emerald-400">+$270k</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-white/60 uppercase">{t('netIncome')}</p>
                    <p className="text-base font-mono font-semibold text-gold-metallic">$90k</p>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <div className="text-center mt-5">
                <Button
                  onClick={() => {
                    setShowDemoModal(false);
                    navigate('/auth?mode=register');
                  }}
                  className="bg-gold-metallic hover:bg-gold-metallic/90 text-navy-deep px-10 py-5 text-sm uppercase tracking-widest rounded-sm font-semibold"
                >
                  {t('tryWithYourProperty')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="md:sticky md:top-0 z-50">
        <div className="bg-sand-warm border-b border-gray-200 h-28 md:h-32 overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 md:px-12">
            <div className="flex items-center justify-between h-28 md:h-32">
              <div className="flex items-center">
                <img 
                  src="/logo-320.webp" 
                  alt="VillaPilot AI" 
                  className="h-40 md:h-44 w-auto object-contain object-top mix-blend-multiply"
                  width="320"
                  height="320"
                  fetchPriority="high"
                />
              </div>

              {/* Desktop Nav */}
              <div className="hidden md:flex items-center gap-6">
                <a href="#features" className="text-base font-medium text-foreground hover:text-gold-metallic transition-colors">{t('features')}</a>
                <a href="#how-it-works" className="text-base font-medium text-foreground hover:text-gold-metallic transition-colors">{t('howItWorks')}</a>
                <a href="#ai-engine" className="text-base font-medium text-foreground hover:text-gold-metallic transition-colors" data-testid="nav-ai-engine">{language === 'es' ? 'IA' : 'AI'}</a>
                <a href="#pricing" className="text-base font-medium text-foreground hover:text-gold-metallic transition-colors">{t('pricing')}</a>
                <button
                  type="button"
                  onClick={() => openRequestDemo('nav')}
                  className="text-base font-medium text-foreground hover:text-gold-metallic transition-colors"
                  data-testid="nav-request-demo"
                >
                  {t('requestDemo')}
                </button>
                <a href="/blog" className="text-base font-medium text-foreground hover:text-gold-metallic transition-colors" data-testid="nav-blog">Blog</a>
                <a href="#contact" className="text-base font-medium text-foreground hover:text-gold-metallic transition-colors">{language === 'es' ? 'Contacto' : 'Contact'}</a>
                
                {/* Language Selector */}
                <div className="flex items-center gap-1 bg-gray-100 rounded-md p-0.5 border border-gray-200" data-testid="marketing-language-selector">
                  <button
                    onClick={() => setLanguage('en')}
                    data-testid="marketing-lang-en"
                    className={`px-3 py-1.5 text-sm font-semibold rounded transition-all ${
                      language === 'en'
                        ? 'bg-navy-deep text-white'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    EN
                  </button>
                  <button
                    onClick={() => setLanguage('es')}
                    data-testid="marketing-lang-es"
                    className={`px-3 py-1.5 text-sm font-semibold rounded transition-all ${
                      language === 'es'
                        ? 'bg-navy-deep text-white'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    ES
                  </button>
                </div>
                
                <Button
                  onClick={() => navigate('/auth?mode=login')}
                  variant="outline"
                  className="rounded-sm"
                  data-testid="nav-sign-in"
                >
                  {t('signIn')}
                </Button>
                <Button
                  onClick={() => navigate('/auth?mode=register')}
                  className="bg-gold-metallic hover:bg-gold-metallic/90 text-navy-deep rounded-sm font-semibold"
                  data-testid="nav-get-started"
                >
                  {t('getStarted')}
                </Button>
              </div>

              {/* Mobile Menu Button + Language Selector */}
              <div className="md:hidden flex items-center gap-2">
                {/* Mobile Language Selector - Always Visible */}
                <div className="flex items-center gap-0.5 bg-gray-100 rounded p-0.5 border border-gray-200" data-testid="mobile-language-selector">
                  <button
                    onClick={() => setLanguage('en')}
                    className={`px-2 py-1 text-xs font-semibold rounded transition-all ${
                      language === 'en'
                        ? 'bg-navy-deep text-white'
                        : 'text-gray-600'
                    }`}
                  >
                    EN
                  </button>
                  <button
                    onClick={() => setLanguage('es')}
                    className={`px-2 py-1 text-xs font-semibold rounded transition-all ${
                      language === 'es'
                        ? 'bg-navy-deep text-white'
                        : 'text-gray-600'
                    }`}
                  >
                    ES
                  </button>
                </div>
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="p-2"
                  aria-label={mobileMenuOpen
                    ? (language === 'es' ? 'Cerrar menú' : 'Close menu')
                    : (language === 'es' ? 'Abrir menú' : 'Open menu')}
                  aria-expanded={mobileMenuOpen}
                  aria-controls="mobile-menu"
                  data-testid="mobile-menu-toggle"
                >
                  {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div id="mobile-menu" className="md:hidden py-4 space-y-4 px-4 bg-sand-warm border-b border-gray-200">
            <a href="#features" className="block text-sm font-medium">{t('features')}</a>
            <a href="#how-it-works" className="block text-sm font-medium">{t('howItWorks')}</a>
            <a href="#ai-engine" className="block text-sm font-medium" data-testid="mobile-nav-ai-engine">{language === 'es' ? 'IA' : 'AI'}</a>
            <a href="#pricing" className="block text-sm font-medium">{t('pricing')}</a>
            <button
              type="button"
              onClick={() => { setMobileMenuOpen(false); openRequestDemo('mobile-nav'); }}
              className="block w-full text-left text-sm font-medium"
              data-testid="mobile-nav-request-demo"
            >
              {t('requestDemo')}
            </button>
            <a href="/blog" className="block text-sm font-medium" data-testid="mobile-nav-blog">Blog</a>
            <a href="#testimonials" className="block text-sm font-medium">{t('testimonials')}</a>
            <Button variant="outline" onClick={() => { setMobileMenuOpen(false); navigate('/auth?mode=login'); }} className="w-full" data-testid="mobile-sign-in-btn">
              {language === 'es' ? 'Iniciar Sesión' : 'Sign In'}
            </Button>
            <Button onClick={() => { setMobileMenuOpen(false); navigate('/auth?mode=register'); }} className="w-full" data-testid="mobile-get-started-btn">{t('getStarted')}</Button>
          </div>
        )}
      </nav>

      <main id="main-content">

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden noise-texture">
        <img
          src="https://images.unsplash.com/photo-1642330012430-3652a78a1ca8?w=1600&q=70&fm=webp&fit=max&auto=format"
          srcSet="https://images.unsplash.com/photo-1642330012430-3652a78a1ca8?w=640&q=65&fm=webp&fit=max&auto=format 640w, https://images.unsplash.com/photo-1642330012430-3652a78a1ca8?w=1024&q=70&fm=webp&fit=max&auto=format 1024w, https://images.unsplash.com/photo-1642330012430-3652a78a1ca8?w=1600&q=70&fm=webp&fit=max&auto=format 1600w"
          sizes="100vw"
          alt=""
          aria-hidden="true"
          fetchPriority="high"
          decoding="async"
          width="1600"
          height="900"
          className="absolute inset-0 w-full h-full object-cover z-0"
          style={{ filter: 'brightness(0.4)' }}
        />
        
        <div className="relative z-10 text-center px-6 md:px-12 max-w-6xl">
          {/* Phase Performance-2 · Hero H1 renders IMMEDIATELY (LCP element).
              The framer-motion opacity animation used to delay first paint by
              ~0.8s, which pushed LCP over 3s. We now render text instantly
              and let CSS handle a subtle fade for the CTA row only. */}
          <div>
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-playfair font-medium tracking-tight text-white mb-6">
              {t('heroTitle')}
              <span className="block mt-2 text-gold-metallic">{t('heroSubtitle')}</span>
            </h1>
            <p className="text-xl md:text-2xl font-light leading-relaxed text-white/90 mb-12 max-w-3xl mx-auto">
              {t('heroDescription')}
            </p>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              className="flex gap-4 justify-center flex-wrap"
            >
              <Button
                onClick={() => navigate('/auth?mode=register')}
                className="bg-gold-metallic hover:bg-gold-metallic/90 text-navy-deep px-12 py-8 text-base uppercase tracking-widest rounded-sm shadow-[0_4px_14px_0_rgba(212,175,55,0.3)] transition-all text-lg font-semibold"
                data-testid="hero-get-started"
              >
                {t('startFreeTrial')}
              </Button>
              <Button
                variant="outline"
                onClick={() => openRequestDemo('hero')}
                className="border-2 border-gold-metallic text-gold-metallic hover:bg-gold-metallic hover:text-navy-deep px-12 py-8 text-base uppercase tracking-widest rounded-sm transition-all text-lg font-semibold"
                data-testid="hero-request-demo"
              >
                {t('requestDemo')}
              </Button>
              <Button
                variant="outline"
                onClick={() => setRoiTourOpen(true)}
                className="border-2 border-white text-white hover:bg-white hover:text-navy-deep px-12 py-8 text-base uppercase tracking-widest rounded-sm transition-all text-lg"
                data-testid="hero-watch-demo-btn"
              >
                {t('watchDemo')}
              </Button>
            </motion.div>
            <p className="mt-6 text-white/70 text-sm">{t('trialNote')}</p>
          </div>
        </div>
      </section>

      {/* AI Valuation Announcement Video — August 2026 launch (newest, on top) */}
      <section className="py-20 md:py-24 bg-gradient-to-b from-navy-deep to-slate-900" data-testid="ai-valuation-video-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="text-center mb-10">
            <p className="text-xs font-bold uppercase tracking-[0.35em] text-gold-metallic mb-3">
              {language === 'es' ? 'Novedad · Septiembre 2026' : 'Product Update · September 2026'}
            </p>
            <h2 className="text-3xl md:text-5xl font-playfair font-medium text-white leading-tight">
              {language === 'es' ? (
                <>Presentamos <span className="text-gold-metallic italic">AI Valuation</span></>
              ) : (
                <>Introducing <span className="text-gold-metallic italic">AI Valuation</span></>
              )}
            </h2>
            <p className="mt-4 text-white/70 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
              {language === 'es'
                ? 'El valor de tu villa no es estático — se mueve cada día.'
                : "Your villa's value isn't static — it's moving every day."}
            </p>
          </div>
          <div
            className="relative rounded-2xl overflow-hidden shadow-[0_20px_60px_-15px_rgba(212,175,55,0.35)] border border-gold-metallic/20"
            style={{ aspectRatio: '16 / 9' }}
          >
            <iframe
              src="https://www.youtube-nocookie.com/embed/fXiKlr-I_9U?rel=0&modestbranding=1"
              title={language === 'es' ? 'Presentando VillaPilot AI Valuation' : 'Introducing VillaPilot AI Valuation'}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              loading="lazy"
              className="absolute inset-0 w-full h-full"
              data-testid="ai-valuation-video-iframe"
            />
          </div>
        </div>
      </section>

      {/* Smart Alerts Announcement Video — July 2026 launch */}
      <section className="py-20 md:py-24 bg-gradient-to-b from-slate-900 to-navy-deep" data-testid="smart-alerts-video-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="text-center mb-10">
            <p className="text-xs font-bold uppercase tracking-[0.35em] text-gold-metallic mb-3">
              {language === 'es' ? 'Novedad · Julio 2026' : 'Product Update · July 2026'}
            </p>
            <h2 className="text-3xl md:text-5xl font-playfair font-medium text-white leading-tight">
              {language === 'es' ? (
                <>Presentamos <span className="text-gold-metallic italic">Smart Alerts</span></>
              ) : (
                <>Introducing <span className="text-gold-metallic italic">Smart Alerts</span></>
              )}
            </h2>
            <p className="mt-4 text-white/70 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
              {language === 'es'
                ? 'Convierte señales dispersas del portafolio en alertas accionables — con severidad, contexto y acción sugerida.'
                : 'Turn scattered portfolio signals into actionable alerts — with severity, context and a suggested next step.'}
            </p>
          </div>
          <div
            className="relative rounded-2xl overflow-hidden shadow-[0_20px_60px_-15px_rgba(212,175,55,0.35)] border border-gold-metallic/20"
            style={{ aspectRatio: '16 / 9' }}
          >
            <iframe
              src="https://www.youtube-nocookie.com/embed/dhTfi_3QnL0?rel=0&modestbranding=1"
              title={language === 'es' ? 'Presentando VillaPilot Smart Alerts' : 'Introducing VillaPilot Smart Alerts'}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              loading="lazy"
              className="absolute inset-0 w-full h-full"
              data-testid="smart-alerts-video-iframe"
            />
          </div>
        </div>
      </section>

      {/* Product Demo Video — YouTube embed */}
      <section className="py-20 md:py-24 bg-gradient-to-b from-navy-deep to-slate-900" data-testid="demo-video-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="text-center mb-10">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-gold-metallic mb-3">
              {language === 'es' ? 'Ve la plataforma en acción' : 'See the platform in action'}
            </p>
            <h2 className="text-3xl md:text-5xl font-playfair font-medium text-white leading-tight">
              {language === 'es'
                ? 'Un vistazo de 60 segundos a VillaPilot AI'
                : 'A 60-second look at VillaPilot AI'}
            </h2>
          </div>
          <div
            className="relative rounded-2xl overflow-hidden shadow-[0_20px_60px_-15px_rgba(212,175,55,0.35)] border border-gold-metallic/20"
            style={{ aspectRatio: '16 / 9' }}
          >
            <iframe
              src="https://www.youtube-nocookie.com/embed/7yy4ltkL1fk?rel=0&modestbranding=1"
              title={language === 'es' ? 'Demo de VillaPilot AI' : 'VillaPilot AI Demo'}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              loading="lazy"
              className="absolute inset-0 w-full h-full"
              data-testid="demo-video-iframe"
            />
          </div>
        </div>
      </section>

      {/* Built For — Luxury Property Owners & Managers */}
      <section
        className="py-20 md:py-28 px-6 md:px-12 bg-sand-warm border-y border-border/50"
        data-testid="built-for-section"
      >
        <div className="max-w-6xl mx-auto">
          <div className="max-w-3xl mb-14">
            <p
              className="text-xs font-bold uppercase tracking-[0.15em] text-gold-text mb-3"
              data-testid="built-for-eyebrow"
            >
              {language === 'es' ? 'DISEÑADO PARA' : 'BUILT FOR'}
            </p>
            <h2
              className="text-3xl md:text-5xl font-playfair font-medium tracking-tight text-foreground"
              data-testid="built-for-title"
            >
              {language === 'es'
                ? 'Diseñado para propietarios y gestores de propiedades de lujo'
                : 'Built for Luxury Property Owners & Managers'}
            </h2>
            <p
              className="text-base md:text-lg text-muted-foreground mt-5 leading-relaxed"
              data-testid="built-for-subtitle"
            >
              {language === 'es'
                ? 'Una plataforma unificada para entender tus números, operar tus villas con precisión y ver tu portafolio al completo en un solo lugar.'
                : 'A single platform to understand your numbers, run your villas with precision, and see your entire portfolio at a glance.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Pillar 1 · AI Financial Insights */}
            <div
              className="bg-white rounded-sm border border-border p-7 md:p-8 flex flex-col transition-colors hover:border-gold-metallic/60"
              data-testid="built-for-card-ai-insights"
            >
              <div className="w-11 h-11 rounded-sm bg-navy-deep text-gold-metallic flex items-center justify-center mb-5">
                <Bot className="w-5 h-5" />
              </div>
              <h3 className="text-xl font-playfair font-medium text-foreground mb-3">
                {language === 'es' ? 'Insights financieros con IA' : 'AI-Powered Financial Insights'}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {language === 'es'
                  ? 'Convierte tus ingresos, gastos y ocupación en recomendaciones claras: dónde ganas, dónde pierdes y qué ajustar este trimestre.'
                  : 'Turn revenue, expenses and occupancy into clear recommendations: where you earn, where you leak, and what to tune this quarter.'}
              </p>
            </div>

            {/* Pillar 2 · Operations & Expense Tracking */}
            <div
              className="bg-white rounded-sm border border-border p-7 md:p-8 flex flex-col transition-colors hover:border-gold-metallic/60"
              data-testid="built-for-card-operations"
            >
              <div className="w-11 h-11 rounded-sm bg-navy-deep text-gold-metallic flex items-center justify-center mb-5">
                <Wrench className="w-5 h-5" />
              </div>
              <h3 className="text-xl font-playfair font-medium text-foreground mb-3">
                {language === 'es'
                  ? 'Operación y control de gastos'
                  : 'Property Operations & Expense Tracking'}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {language === 'es'
                  ? 'Housekeeping, mantenimiento, inventario y proveedores en un flujo único, con cada gasto categorizado y auditable por propiedad.'
                  : 'Housekeeping, maintenance, inventory and vendors in one flow — every expense categorised and auditable, villa by villa.'}
              </p>
            </div>

            {/* Pillar 3 · Portfolio Visibility & Reporting */}
            <div
              className="bg-white rounded-sm border border-border p-7 md:p-8 flex flex-col transition-colors hover:border-gold-metallic/60"
              data-testid="built-for-card-portfolio"
            >
              <div className="w-11 h-11 rounded-sm bg-navy-deep text-gold-metallic flex items-center justify-center mb-5">
                <TrendingUp className="w-5 h-5" />
              </div>
              <h3 className="text-xl font-playfair font-medium text-foreground mb-3">
                {language === 'es'
                  ? 'Visibilidad y rendimiento del portafolio'
                  : 'Portfolio Visibility & Performance Reporting'}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {language === 'es'
                  ? 'Compara propiedades lado a lado, exporta reportes limpios para socios y revisa tu rendimiento consolidado sin cruzar hojas de cálculo.'
                  : 'Compare properties side by side, export clean reports for partners, and review consolidated performance without juggling spreadsheets.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Demo Section */}

      {/* Features Section */}
      <section id="features" className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-gold-text mb-4">{t('features').toUpperCase()}</p>
            <h2 className="text-4xl md:text-5xl font-playfair font-medium tracking-tight text-foreground mb-4">
              {t('featuresTitle')}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('featuresSubtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card className="group relative overflow-hidden rounded-sm border border-border bg-white p-8 hover:border-gold-metallic/30 hover:shadow-lg transition-all duration-500 h-full">
                    <div className="mb-6">
                      <div className="w-14 h-14 rounded-sm bg-navy-deep/10 flex items-center justify-center group-hover:bg-gold-metallic/10 transition-colors">
                        <Icon className="w-7 h-7 text-navy-deep group-hover:text-gold-metallic transition-colors" />
                      </div>
                    </div>
                    <h3 className="text-2xl font-playfair mb-4">{t(feature.titleKey)}</h3>
                    <p className="text-base leading-relaxed text-muted-foreground">{t(feature.descriptionKey)}</p>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section id="how-it-works" className="py-24 md:py-32 px-6 md:px-12 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-gold-text mb-4">{t('howItWorks').toUpperCase()}</p>
            <h2 className="text-4xl md:text-5xl font-playfair font-medium tracking-tight text-foreground">
              {t('getStartedInMinutes')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {[
              { step: "01", titleKey: "stepCreateAccount", descKey: "stepCreateAccountDesc" },
              { step: "02", titleKey: "stepInviteTeam", descKey: "stepInviteTeamDesc" },
              { step: "03", titleKey: "stepStartManaging", descKey: "stepStartManagingDesc" }
            ].map((item, idx) => (
              <div key={idx} className="text-center">
                <div className="w-20 h-20 rounded-full bg-gold-metallic/10 flex items-center justify-center mx-auto mb-6">
                  <span className="text-3xl font-playfair font-medium text-gold-text">{item.step}</span>
                </div>
                <h3 className="text-2xl font-playfair mb-3">{t(item.titleKey)}</h3>
                <p className="text-muted-foreground">{t(item.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Powered by Claude Section */}
      <section id="ai-engine" className="py-24 md:py-32 px-6 md:px-12 bg-gradient-to-b from-[#0a0f1a] to-navy-deep text-white relative overflow-hidden" data-testid="ai-engine-section">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #D4AF37 0%, transparent 50%), radial-gradient(circle at 80% 80%, #D4AF37 0%, transparent 50%)' }}></div>

        <div className="max-w-6xl mx-auto relative">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-gold-text mb-4">
              {language === 'es' ? 'EL MOTOR DETRÁS DE TU PLATAFORMA' : 'THE ENGINE BEHIND YOUR PLATFORM'}
            </p>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-playfair font-medium tracking-tight mb-6">
              {language === 'es' ? (
                <>Funcionamos con <span className="text-gold-metallic">Claude</span></>
              ) : (
                <>Powered by <span className="text-gold-metallic">Claude</span></>
              )}
            </h2>
            <p className="text-lg md:text-xl text-white/70 max-w-3xl mx-auto leading-relaxed">
              {language === 'es'
                ? 'Cuando construimos VillaPilot AI, enfrentamos una decisión crítica: ¿qué modelo de IA daría vida al cerebro de la plataforma? Elegimos Claude Sonnet 4.5 de Anthropic. Esta es la razón.'
                : 'When we built VillaPilot AI, we faced a critical decision: which AI model would power the brain of the platform? We chose Claude Sonnet 4.5 by Anthropic. Here\'s why.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-12">
            {[
              {
                num: '01',
                titleEs: 'Razonamiento Financiero Superior',
                titleEn: 'Superior Financial Reasoning',
                descEs: 'Claude supera a ChatGPT en razonamiento multi-paso — exactamente lo que se necesita para conciliar calendarios de reservas, separar tarifas de limpieza, asignar IVA entre jurisdicciones y proyectar ocupación. Menos números inventados. Respuestas más confiables.',
                descEn: 'Claude consistently outperforms ChatGPT on multi-step reasoning — the exact thinking required to reconcile booking calendars, separate cleaning fees, allocate VAT across jurisdictions, and forecast occupancy. Fewer hallucinated numbers. More trustworthy answers.',
              },
              {
                num: '02',
                titleEs: 'Ventana de Contexto de 200,000 Tokens',
                titleEn: '200,000-Token Context Window',
                descEs: 'Claude puede mantener aproximadamente 500 páginas de tus datos en mente a la vez: cada reserva, gasto, mensaje y reporte. La ventana estándar de ChatGPT es una fracción. Esto significa respuestas reales a través de toda tu historia.',
                descEn: 'Claude can hold roughly 500 pages of your data in mind at once: every booking, expense, message, and report. ChatGPT\'s standard window is a fraction of that — meaning real answers across your full property history.',
              },
              {
                num: '03',
                titleEs: 'Privacidad y Custodia de Datos',
                titleEn: 'Privacy & Data Stewardship',
                descEs: 'La política de Anthropic es clara: tus datos no se usan para entrenar sus modelos. Para una plataforma que maneja información de huéspedes, ingresos e impuestos en EAU, España y más, eso no es negociable.',
                descEn: "Anthropic's policy is clear: your data is not used to train their models. For a platform handling guest information, revenue, and tax data across UAE, Spain and beyond, that distinction is non-negotiable.",
              },
              {
                num: '04',
                titleEs: 'Precisión sobre la que Puedes Facturar',
                titleEn: 'Accuracy You Can Bill On',
                descEs: 'Claude está diseñado para decir "no lo sé" en lugar de inventar una respuesta. Cuando estimas un impuesto o resumes un trimestre, obtienes conclusiones razonadas respaldadas por tus datos — no ficción confiada.',
                descEn: 'Claude is engineered to say "I don\'t know" instead of inventing an answer. When you estimate a tax liability or summarize a quarter, you get reasoned conclusions backed by your data — not confident fiction.',
              },
            ].map((card, idx) => (
              <div
                key={idx}
                data-testid={`ai-engine-card-${idx}`}
                className="group relative bg-white/[0.03] backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:border-gold-metallic/40 hover:bg-white/[0.06] transition-all duration-300"
              >
                <div className="flex items-start gap-5">
                  <div className="shrink-0 w-14 h-14 rounded-xl bg-gold-metallic/10 border border-gold-metallic/30 flex items-center justify-center group-hover:bg-gold-metallic/20 transition-colors">
                    <span className="text-lg font-playfair font-medium text-gold-metallic">{card.num}</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl md:text-2xl font-playfair font-medium mb-3 text-white">
                      {language === 'es' ? card.titleEs : card.titleEn}
                    </h3>
                    <p className="text-white/70 leading-relaxed text-[15px]">
                      {language === 'es' ? card.descEs : card.descEn}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="relative bg-gradient-to-br from-gold-metallic/15 to-gold-metallic/5 border border-gold-metallic/30 rounded-2xl p-8 md:p-10 text-center">
            <p className="text-lg md:text-xl leading-relaxed text-white/90 max-w-4xl mx-auto">
              {language === 'es' ? (
                <>
                  <span className="text-gold-metallic font-semibold">ChatGPT es un generalista brillante. Claude es un especialista más afilado</span> para el trabajo estructurado y de alto riesgo que tus villas exigen. Esta elección no fue por moda — fue para darte el copiloto más confiable que pudiéramos poner detrás del panel.
                </>
              ) : (
                <>
                  <span className="text-gold-metallic font-semibold">ChatGPT is a brilliant generalist. Claude is a sharper specialist</span> for the structured, high-stakes work your villas demand. This choice wasn't about hype — it was about giving you the most reliable co-pilot we could put behind the dashboard.
                </>
              )}
            </p>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-gold-text mb-4">{t('pricing').toUpperCase()}</p>
            <h2 className="text-4xl md:text-5xl font-playfair font-medium tracking-tight text-foreground mb-4">
              {t('pricingTitle')}
            </h2>
            <p className="text-lg text-muted-foreground">{t('pricingSubtitle')}</p>
          </div>

          {/* Audience Toggle */}
          <div className="flex justify-center mb-10" data-testid="pricing-audience-toggle">
            <div className="inline-flex items-center bg-white border border-border rounded-full p-1 shadow-sm">
              <button
                onClick={() => setPricingAudience('owners')}
                data-testid="pricing-tab-owners"
                className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all ${
                  pricingAudience === 'owners'
                    ? 'bg-navy-deep text-white shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {language === 'es' ? 'Para Propietarios' : 'For Owners'}
              </button>
              <button
                onClick={() => setPricingAudience('managers')}
                data-testid="pricing-tab-managers"
                className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all ${
                  pricingAudience === 'managers'
                    ? 'bg-navy-deep text-white shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {language === 'es' ? 'Para Administradoras' : 'For Property Managers'}
              </button>
            </div>
          </div>

          {/* Owners Plans (3 cards) */}
          {pricingAudience === 'owners' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto" data-testid="pricing-grid-owners">
              {pricingPlans.owners.map((plan, idx) => {
                const isPremiumPlan = plan.name === 'Premium';
                return (
                <Card key={idx} className={`relative rounded-sm border p-8 ${plan.popular ? 'border-gold-metallic shadow-2xl lg:scale-105' : 'border-border'} bg-white transition-all hover:shadow-xl`}>
                  {plan.popular && (
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                      <span className="bg-gold-metallic text-navy-deep px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
                        {t('mostPopular')}
                      </span>
                    </div>
                  )}
                  <h3 className="text-2xl font-playfair mb-2">{plan.name}</h3>
                  <div className="mb-6">
                    <span className="text-5xl font-playfair font-medium">{plan.price}</span>
                    <span className="text-muted-foreground">{plan.period}</span>
                  </div>
                  <ul className="space-y-4 mb-8">
                    {plan.features.map((feature, fidx) => (
                      <li key={fidx} className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-emerald-rich flex-shrink-0" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={() =>
                      isPremiumPlan
                        ? openRequestDemo('pricing-premium')
                        : navigate('/auth?mode=register')
                    }
                    className={`w-full rounded-sm py-6 font-semibold ${plan.popular ? 'bg-gold-metallic hover:bg-gold-metallic/90 text-navy-deep' : 'bg-navy-deep hover:bg-navy-deep/90 text-white'}`}
                    data-testid={isPremiumPlan ? 'pricing-premium-talk-to-sales' : `pricing-${plan.name.toLowerCase()}-get-started`}
                  >
                    {isPremiumPlan
                      ? t('talkToSales')
                      : (language === 'es' ? 'Comenzar Ahora' : 'Get Started')}
                  </Button>
                  <p className="text-xs text-center text-muted-foreground mt-3">
                    {isPremiumPlan
                      ? (language === 'es'
                          ? 'Hablamos contigo en menos de 24h hábiles'
                          : 'We reply within one business day')
                      : (language === 'es' ? 'Prueba gratuita de 14 días' : '14-day free trial')}
                  </p>
                </Card>
                );
              })}
            </div>
          )}

          {/* Managers Plans (2 cards + annual toggle for Agency) */}
          {pricingAudience === 'managers' && (
            <>
              <div className="flex justify-center mb-8" data-testid="pricing-billing-toggle">
                <div className="inline-flex items-center bg-white border border-border rounded-full p-1 text-sm">
                  <button
                    onClick={() => setPricingAnnual(false)}
                    data-testid="billing-monthly"
                    className={`px-4 py-1.5 rounded-full font-medium transition-all ${
                      !pricingAnnual ? 'bg-muted text-foreground' : 'text-muted-foreground'
                    }`}
                  >
                    {language === 'es' ? 'Mensual' : 'Monthly'}
                  </button>
                  <button
                    onClick={() => setPricingAnnual(true)}
                    data-testid="billing-annual"
                    className={`px-4 py-1.5 rounded-full font-medium transition-all flex items-center gap-2 ${
                      pricingAnnual ? 'bg-muted text-foreground' : 'text-muted-foreground'
                    }`}
                  >
                    {language === 'es' ? 'Anual' : 'Annual'}
                    <span className="bg-emerald-100 text-emerald-700 text-[10px] font-bold px-1.5 py-0.5 rounded">
                      -15%
                    </span>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto" data-testid="pricing-grid-managers">
                {pricingPlans.managers.map((plan, idx) => {
                  if (plan.isAgencyPlan) {
                    const currentPrice = pricingAnnual ? plan.priceAnnualPerVilla : plan.priceMonthly;
                    return (
                      <Card key={idx} className="relative rounded-sm border-2 border-purple-300 bg-purple-50/30 p-8 transition-all hover:shadow-xl" data-testid="pricing-card-agency">
                        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                          <span className="bg-purple-600 text-white px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
                            {language === 'es' ? 'PARA ADMINISTRADORAS' : 'FOR PROPERTY MANAGERS'}
                          </span>
                        </div>
                        <h3 className="text-2xl font-playfair mb-1">{plan.name}</h3>
                        <p className="text-sm text-muted-foreground mb-6">
                          {language === 'es' ? plan.subtitleEs : plan.subtitleEn}
                        </p>
                        <div className="mb-2">
                          {pricingAnnual && (
                            <span className="text-2xl font-playfair text-muted-foreground line-through mr-2">
                              ${plan.priceMonthly}
                            </span>
                          )}
                          <span className="text-5xl font-playfair font-medium">${currentPrice}</span>
                          <span className="text-muted-foreground">
                            {language === 'es' ? '/mes' : '/month'}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mb-6">
                          {language === 'es'
                            ? 'Mínimo 10 propiedades · Precio por volumen disponible'
                            : 'Minimum 10 properties · Volume pricing available'}
                        </p>
                        <ul className="space-y-4 mb-8">
                          {plan.features.map((feature, fidx) => (
                            <li key={fidx} className="flex items-start gap-3">
                              <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                              <span className="text-sm">{feature}</span>
                            </li>
                          ))}
                        </ul>
                        <Button
                          onClick={() => setAgencyDemoOpen(true)}
                          className="w-full rounded-sm py-6 font-semibold bg-purple-600 hover:bg-purple-700 text-white"
                          data-testid="agency-book-demo-btn"
                        >
                          {language === 'es' ? 'Agenda una Demo' : 'Book a Demo'}
                        </Button>
                        <p className="text-xs text-center text-muted-foreground mt-3">
                          {language === 'es'
                            ? 'Piloto de 14 días sin riesgo · Sin contratos a largo plazo durante trial'
                            : '14-day risk-free pilot · No long-term lock-in during trial'}
                        </p>
                      </Card>
                    );
                  }
                  // Manager Pro
                  return (
                    <Card key={idx} className="relative rounded-sm border border-purple-300 bg-purple-50/30 p-8 transition-all hover:shadow-xl" data-testid="pricing-card-manager-pro">
                      <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                        <span className="bg-purple-600 text-white px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
                          {language === 'es' ? 'MANAGERS' : 'MANAGERS'}
                        </span>
                      </div>
                      <h3 className="text-2xl font-playfair mb-2">{plan.name}</h3>
                      <div className="mb-6">
                        <span className="text-5xl font-playfair font-medium">{plan.price}</span>
                        <span className="text-muted-foreground">{plan.period}</span>
                      </div>
                      <ul className="space-y-4 mb-8">
                        {plan.features.map((feature, fidx) => (
                          <li key={fidx} className="flex items-center gap-3">
                            <CheckCircle className="w-5 h-5 text-emerald-rich flex-shrink-0" />
                            <span className="text-sm">{feature}</span>
                          </li>
                        ))}
                      </ul>
                      <Button
                        onClick={() => navigate('/auth?mode=register')}
                        className="w-full rounded-sm py-6 font-semibold bg-purple-600 hover:bg-purple-700 text-white"
                      >
                        {language === 'es' ? 'Comenzar Ahora' : 'Get Started'}
                      </Button>
                      <p className="text-xs text-center text-muted-foreground mt-3">
                        {language === 'es' ? 'Prueba gratuita de 14 días' : '14-day free trial'}
                      </p>
                    </Card>
                  );
                })}
              </div>
            </>
          )}

          {/* Trust Badges */}
          <div className="mt-12 flex flex-col md:flex-row items-center justify-center gap-8 md:gap-12">
            <div className="flex items-center gap-3 text-muted-foreground">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                <RefreshCcw className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="font-medium text-foreground text-sm">
                  {language === 'es' ? 'Garantía de Devolución' : 'Money-back Guarantee'}
                </p>
                <p className="text-xs">
                  {language === 'es' 
                    ? 'Pruébalo sin riesgo. Si no es para ti, solicita reembolso en 14 días.' 
                    : 'Try it risk-free. If it\'s not for you, request a refund within 14 days.'}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3 text-muted-foreground">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Lock className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-foreground text-sm">
                  {language === 'es' ? 'Pago Seguro' : 'Secure Checkout'}
                </p>
                <p className="text-xs">
                  {language === 'es' 
                    ? 'Checkout encriptado 256-bit con Stripe.' 
                    : '256-bit encrypted checkout with Stripe.'}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3 text-muted-foreground">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="font-medium text-foreground text-sm">
                  {language === 'es' ? 'Cancela Cuando Quieras' : 'Cancel Anytime'}
                </p>
                <p className="text-xs">
                  {language === 'es' 
                    ? 'Sin contratos. Sin compromisos. Cancela en cualquier momento.' 
                    : 'No contracts. No commitments. Cancel anytime.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-24 md:py-32 px-6 md:px-12 bg-navy-deep text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-gold-text mb-4">{t('testimonials').toUpperCase()}</p>
            <h2 className="text-4xl md:text-5xl font-playfair font-medium tracking-tight">
              {t('testimonialsTitle')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, idx) => (
              <div key={idx} className="p-8">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-gold-metallic text-gold-metallic" />
                  ))}
                </div>
                <p className="text-white/90 mb-6 leading-relaxed">{testimonial.text}</p>
                <div className="flex items-center gap-3">
                  <img src={testimonial.image} alt={testimonial.name} className="w-12 h-12 rounded-full object-cover" loading="lazy" decoding="async" width="48" height="48" />
                  <div>
                    <p className="font-medium text-white">{testimonial.name}</p>
                    <p className="text-sm text-white/60">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-gradient-to-br from-navy-deep to-navy-deep/90">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-4xl md:text-5xl font-playfair font-medium tracking-tight mb-6">
            {t('ctaTitle')}
          </h2>
          <p className="text-xl font-light leading-relaxed text-white/80 mb-12">
            {t('ctaSubtitle')}
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Button
              onClick={() => navigate('/auth?mode=register')}
              className="bg-gold-metallic hover:bg-gold-metallic/90 text-navy-deep px-12 py-8 text-base uppercase tracking-widest rounded-sm shadow-[0_4px_14px_0_rgba(212,175,55,0.3)] font-semibold"
              data-testid="final-cta"
            >
              {t('startFreeTrial')}
            </Button>
            <Button
              variant="outline"
              onClick={() => openRequestDemo('final-cta')}
              className="border-2 border-white text-white hover:bg-white hover:text-navy-deep px-12 py-8 text-base uppercase tracking-widest rounded-sm font-semibold"
              data-testid="final-cta-request-demo"
            >
              {t('requestDemo')}
            </Button>
          </div>
          <p className="mt-6 text-white/60 text-sm">{t('trialInfo')}</p>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 px-6 md:px-12 bg-gradient-to-b from-[#0a0f1a] to-navy-deep">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-playfair font-medium text-white mb-4">
              {language === 'es' ? 'Contáctanos' : 'Contact Us'}
            </h2>
            <p className="text-lg text-white/60 max-w-2xl mx-auto">
              {language === 'es' 
                ? '¿Tienes preguntas? Estamos aquí para ayudarte.'
                : 'Have questions? We\'re here to help.'}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div className="flex items-start gap-4">
                <div className="p-3 bg-emerald-500/20 rounded-lg">
                  <Mail className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Email</h3>
                  <a href="mailto:support@villapilot.ai" className="text-emerald-400 hover:text-emerald-300 transition-colors">
                    support@villapilot.ai
                  </a>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="p-3 bg-emerald-500/20 rounded-lg">
                  <MapPin className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">
                    {language === 'es' ? 'Ubicación' : 'Location'}
                  </h3>
                  <p className="text-white/60">Costa Rica</p>
                </div>
              </div>

              <div className="p-6 bg-white/5 rounded-xl border border-white/10">
                <h3 className="text-white font-semibold mb-3">
                  {language === 'es' ? '¿Por qué VillaPilot AI?' : 'Why VillaPilot AI?'}
                </h3>
                <ul className="space-y-2 text-white/60 text-sm">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                    {language === 'es' ? 'Soporte en español e inglés' : 'Spanish & English support'}
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                    {language === 'es' ? 'Respuesta en menos de 24 horas' : 'Response within 24 hours'}
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                    {language === 'es' ? 'Prueba gratuita de 14 días' : '14-day free trial'}
                  </li>
                </ul>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <form onSubmit={handleContactSubmit} className="space-y-4" data-testid="contact-form">
                {/* Success Banner */}
                {contactSuccess && (
                  <div data-testid="contact-success-banner" className="p-4 bg-emerald-500/20 border border-emerald-400/40 rounded-lg flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                    <p className="text-emerald-300 text-sm">
                      {language === 'es' 
                        ? '¡Mensaje enviado correctamente! Te responderemos pronto.' 
                        : 'Message sent successfully! We\'ll respond soon.'}
                    </p>
                  </div>
                )}
                
                {/* Error Banner */}
                {contactError && (
                  <div data-testid="contact-error-banner" className="p-4 bg-red-500/20 border border-red-400/40 rounded-lg flex items-center gap-3">
                    <X className="w-5 h-5 text-red-400 flex-shrink-0" />
                    <p className="text-red-300 text-sm">{contactError}</p>
                  </div>
                )}
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white/80 text-sm mb-2">
                      {language === 'es' ? 'Nombre' : 'Name'}
                    </label>
                    <input
                      type="text"
                      required
                      data-testid="contact-name-input"
                      value={contactForm.name}
                      onChange={(e) => { setContactForm({...contactForm, name: e.target.value}); setContactError(''); }}
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-emerald-400 transition-colors"
                      placeholder={language === 'es' ? 'Tu nombre' : 'Your name'}
                    />
                  </div>
                  <div>
                    <label className="block text-white/80 text-sm mb-2">Email</label>
                    <input
                      type="email"
                      required
                      data-testid="contact-email-input"
                      value={contactForm.email}
                      onChange={(e) => { setContactForm({...contactForm, email: e.target.value}); setContactError(''); }}
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-emerald-400 transition-colors"
                      placeholder="tu@email.com"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-white/80 text-sm mb-2">
                    {language === 'es' ? 'Asunto' : 'Subject'}
                  </label>
                  <input
                    type="text"
                    required
                    data-testid="contact-subject-input"
                    value={contactForm.subject}
                    onChange={(e) => { setContactForm({...contactForm, subject: e.target.value}); setContactError(''); }}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-emerald-400 transition-colors"
                    placeholder={language === 'es' ? '¿En qué podemos ayudarte?' : 'How can we help?'}
                  />
                </div>
                
                <div>
                  <label className="block text-white/80 text-sm mb-2">
                    {language === 'es' ? 'Mensaje' : 'Message'}
                    <span className="text-white/30 ml-2 text-xs">({language === 'es' ? 'mín. 10 caracteres' : 'min. 10 chars'})</span>
                  </label>
                  <textarea
                    required
                    rows={5}
                    data-testid="contact-message-input"
                    value={contactForm.message}
                    onChange={(e) => { setContactForm({...contactForm, message: e.target.value}); setContactError(''); }}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-emerald-400 transition-colors resize-none"
                    placeholder={language === 'es' ? 'Escribe tu mensaje aquí...' : 'Write your message here...'}
                  />
                </div>
                
                <Button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={sendingContact}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                >
                  {sendingContact ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      {language === 'es' ? 'Enviando...' : 'Sending...'}
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      {language === 'es' ? 'Enviar Mensaje' : 'Send Message'}
                    </>
                  )}
                </Button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      </main>

      {/* Footer */}
      <footer className="py-12 px-6 md:px-12 bg-navy-deep border-t border-white/10 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center mb-4">
                <img 
                  src="/logo-160.webp" 
                  alt="VillaPilot AI" 
                  className="h-20 w-auto object-contain"
                  width="160"
                  height="160"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <p className="text-sm text-white/60">{t('footerTagline')}</p>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-sm uppercase tracking-wider">{t('product')}</h4>
              <ul className="space-y-2 text-sm text-white/60">
                <li><a href="#features" className="hover:text-white">{t('features')}</a></li>
                <li><a href="#pricing" className="hover:text-white">{t('pricing')}</a></li>
                <li><a href="/blog" className="hover:text-white" data-testid="footer-blog-link">Blog</a></li>
                <li><a href="#" className="hover:text-white">{t('mobileApp')}</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-sm uppercase tracking-wider">{t('company')}</h4>
              <ul className="space-y-2 text-sm text-white/60">
                <li><a href="#" className="hover:text-white">{t('about')}</a></li>
                <li><a href="#contact" className="hover:text-white">{language === 'es' ? 'Contacto' : 'Contact'}</a></li>
                <li><a href="mailto:support@villapilot.ai" className="hover:text-emerald-400 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  support@villapilot.ai
                </a></li>
                <li>
                  <a 
                    href="https://www.instagram.com/villapilot.ai/" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="hover:text-white flex items-center gap-2 transition-colors"
                    data-testid="instagram-link"
                  >
                    <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                      <defs>
                        <linearGradient id="ig-grad-sm" x1="0%" y1="100%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="#FED576"/>
                          <stop offset="26%" stopColor="#F47133"/>
                          <stop offset="61%" stopColor="#BC3081"/>
                          <stop offset="100%" stopColor="#4C63D2"/>
                        </linearGradient>
                      </defs>
                      <path fill="url(#ig-grad-sm)" d="M12 2.163c3.204 0 3.584.012 4.849.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.849.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                    </svg>
                    @villapilot.ai
                  </a>
                </li>
                <li>
                  <a 
                    href="https://www.linkedin.com/company/113256467/" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="hover:text-white flex items-center gap-2 transition-colors group"
                    data-testid="linkedin-link"
                  >
                    <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="#0A66C2" aria-hidden="true">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                    LinkedIn
                  </a>
                </li>
                <li>
                  <a 
                    href="https://www.youtube.com/@VillaPilotAI" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="hover:text-white flex items-center gap-2 transition-colors"
                    data-testid="youtube-link"
                  >
                    <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814z" fill="#FF0000"/>
                      <path d="M9.545 15.568V8.432L15.818 12l-6.273 3.568z" fill="#FFFFFF"/>
                    </svg>
                    YouTube
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-sm uppercase tracking-wider">{t('legal')}</h4>
              <ul className="space-y-2 text-sm text-white/60">
                <li><a href="/privacy" className="hover:text-white">{t('privacy')}</a></li>
                <li><a href="/terms" className="hover:text-white">{t('terms')}</a></li>
                <li><a href="/security" className="hover:text-white">{t('security')}</a></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-white/60">© 2026 VillaPilot AI. {t('footerCopyright')}</p>
            <div className="flex items-center gap-4">
              <a 
                href="https://www.instagram.com/villapilot.ai/" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="transition-opacity hover:opacity-80"
                aria-label="Instagram"
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24" aria-hidden="true">
                  <defs>
                    <linearGradient id="ig-grad-lg" x1="0%" y1="100%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#FED576"/>
                      <stop offset="26%" stopColor="#F47133"/>
                      <stop offset="61%" stopColor="#BC3081"/>
                      <stop offset="100%" stopColor="#4C63D2"/>
                    </linearGradient>
                  </defs>
                  <path fill="url(#ig-grad-lg)" d="M12 2.163c3.204 0 3.584.012 4.849.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.849.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                </svg>
              </a>
              <a 
                href="https://www.linkedin.com/company/113256467/" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="transition-opacity hover:opacity-80"
                aria-label="LinkedIn"
                data-testid="linkedin-social-icon"
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="#0A66C2" aria-hidden="true">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
              <a 
                href="https://www.youtube.com/@VillaPilotAI" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="transition-opacity hover:opacity-80"
                aria-label="YouTube"
                data-testid="youtube-social-icon"
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814z" fill="#FF0000"/>
                  <path d="M9.545 15.568V8.432L15.818 12l-6.273 3.568z" fill="#FFFFFF"/>
                </svg>
              </a>
            </div>
          </div>
        </div>
      </footer>
      
      {/* Toast notifications */}
      <Toaster />

      {/* Agency Demo Modal — triggered only from Property Manager (Agency) tier */}
      <AgencyDemoModal
        open={agencyDemoOpen}
        onOpenChange={setAgencyDemoOpen}
        billingPreference={pricingAnnual ? 'annual' : 'monthly'}
      />

      {/* Request Demo Modal — generic demo request from hero / nav / final CTA / Premium plan */}
      <RequestDemoModal
        open={requestDemoOpen}
        onOpenChange={setRequestDemoOpen}
        source={requestDemoSource}
      />

      {/* ROI-first Guided Product Tour — 6 static screens (no API calls) */}
      <RoiGuidedDemoModal
        open={roiTourOpen}
        onOpenChange={setRoiTourOpen}
        onRequestDemo={(src) => openRequestDemo(src || 'tour-s6')}
        onGetStarted={() => navigate('/auth?mode=register')}
      />
    </div>
  );
};

export default MarketingWebsite;
